import { STORAGE_KEYS } from "../brand";
import type { PlatformAuditFilters, PlatformAuditPage } from "./platform-audit-types";
import type {
  PlatformAuditSummary,
  PlatformConsoleOverview,
  PlatformContext,
  PlatformIdentityStatus,
  PlatformPlanSummary,
  PlatformProvisioningSummary,
  PlatformRegistrationDetail,
  PlatformRegistrationFilters,
  PlatformRegistrationSummary,
  PlatformProvisioningRecoveryRequest,
  PlatformProvisioningRecoveryResponse,
  PlatformTenantDetail,
  PlatformTenantFilters,
  PlatformTenantSummary
} from "./platform-types";

function apiError(response: Response, payload: unknown): Error {
  const problem = payload && typeof payload === "object"
    ? payload as Record<string, unknown>
    : {};
  const detail = typeof problem.detail === "string" ? problem.detail : null;
  const title = typeof problem.title === "string" ? problem.title : null;
  const correlationId = typeof problem.correlationId === "string"
    ? problem.correlationId
    : response.headers.get("X-Correlation-Id") ?? undefined;
  const error = new Error(detail ?? title ?? "Platform request failed.");
  Object.assign(error, {
    status: response.status,
    problem: { ...problem, correlationId }
  });
  return error;
}

async function platformFetch<T>(
  path: string,
  token: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(path, {
    ...options,
    headers: {
      Accept: "application/json",
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      Authorization: `Bearer ${token}`,
      ...(options.headers ?? {})
    }
  });

  if (response.status === 204) return null as T;
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw apiError(response, payload);
  return payload as T;
}

export function getPlatformAccessToken(): string | null {
  return sessionStorage.getItem(STORAGE_KEYS.platformAccessToken);
}

export function setPlatformAccessToken(token: string): void {
  sessionStorage.setItem(STORAGE_KEYS.platformAccessToken, token);
}

export function clearPlatformSession(): void {
  sessionStorage.removeItem(STORAGE_KEYS.platformAccessToken);
}

export function getPlatformIdentityStatus(identityToken: string): Promise<PlatformIdentityStatus> {
  return platformFetch<PlatformIdentityStatus>(
    "/api/v1/platform-context/status",
    identityToken
  );
}

export function selectPlatformContext(identityToken: string): Promise<PlatformContext> {
  return platformFetch<PlatformContext>(
    "/api/v1/platform-context/select",
    identityToken,
    { method: "POST" }
  );
}

export function getPlatformConsoleOverview(token: string): Promise<PlatformConsoleOverview> {
  return platformFetch<PlatformConsoleOverview>("/api/v1/platform-console/overview", token);
}

export async function getPlatformTenants(
  token: string,
  filters: PlatformTenantFilters = {}
): Promise<PlatformTenantSummary[]> {
  const query = new URLSearchParams({ limit: "200" });
  for (const [key, value] of Object.entries(filters)) {
    const normalized = value?.trim();
    if (normalized) query.set(key, normalized);
  }
  const response = await platformFetch<{ items: PlatformTenantSummary[] }>(
    `/api/v1/platform-console/tenants?${query}`,
    token
  );
  return response.items;
}

export function getPlatformTenant(
  token: string,
  tenantId: string
): Promise<PlatformTenantDetail> {
  return platformFetch<PlatformTenantDetail>(
    `/api/v1/platform-console/tenants/${encodeURIComponent(tenantId)}`,
    token
  );
}

export async function getPlatformRegistrations(
  token: string,
  filters: PlatformRegistrationFilters = {}
): Promise<PlatformRegistrationSummary[]> {
  const query = new URLSearchParams({ limit: "200" });
  if (filters.search?.trim()) query.set("search", filters.search.trim());
  if (filters.signupStatus?.trim()) query.set("signupStatus", filters.signupStatus.trim());
  if (filters.attentionOnly) query.set("attentionOnly", "true");
  const response = await platformFetch<{ items: PlatformRegistrationSummary[] }>(
    `/api/v1/platform-console/registrations?${query}`,
    token
  );
  return response.items;
}

export function getPlatformRegistration(
  token: string,
  registrationId: string
): Promise<PlatformRegistrationDetail> {
  return platformFetch<PlatformRegistrationDetail>(
    `/api/v1/platform-console/registrations/${encodeURIComponent(registrationId)}`,
    token
  );
}

export function reconcilePlatformProvisioning(
  token: string,
  registrationId: string,
  body: PlatformProvisioningRecoveryRequest,
  idempotencyKey: string
): Promise<PlatformProvisioningRecoveryResponse> {
  return platformFetch<PlatformProvisioningRecoveryResponse>(
    `/api/v1/platform-console/registrations/${encodeURIComponent(registrationId)}/recovery/reconcile`,
    token,
    {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey },
      body: JSON.stringify(body)
    }
  );
}

export async function getPlatformProvisioning(token: string): Promise<PlatformProvisioningSummary[]> {
  const response = await platformFetch<{ items: PlatformProvisioningSummary[] }>(
    "/api/v1/platform-console/provisioning?limit=150",
    token
  );
  return response.items;
}

export async function getPlatformPlans(token: string): Promise<PlatformPlanSummary[]> {
  const response = await platformFetch<{ items: PlatformPlanSummary[] }>(
    "/api/v1/platform-console/plans",
    token
  );
  return response.items;
}

export async function getPlatformAudit(token: string): Promise<PlatformAuditSummary[]> {
  const response = await platformFetch<{ items: PlatformAuditSummary[] }>(
    "/api/v1/platform-console/audit?limit=150",
    token
  );
  return response.items;
}

export function getPlatformAuditEvents(
  token: string,
  filters: PlatformAuditFilters = {},
  cursor: string | null = null,
  signal?: AbortSignal
): Promise<PlatformAuditPage> {
  const pageSize = Math.min(100, Math.max(1, filters.pageSize ?? 50));
  const query = new URLSearchParams({ pageSize: String(pageSize) });
  const values: Array<[string, string | undefined]> = [
    ["tenantId", filters.tenantId],
    ["actorUserId", filters.actorUserId],
    ["action", filters.action],
    ["entityType", filters.entityType],
    ["entityId", filters.entityId],
    ["from", filters.from],
    ["to", filters.to]
  ];
  for (const [key, value] of values) {
    const normalized = value?.trim();
    if (normalized) query.set(key, normalized);
  }
  if (cursor) query.set("cursor", cursor);

  return platformFetch<PlatformAuditPage>(
    `/api/v1/platform-console/audit/events?${query}`,
    token,
    { signal }
  );
}

export {
  createPlatformIdempotencyKey,
  executePlatformPlanCommand,
  executePlatformSubscriptionCommand,
  getPlatformPlanDetail,
  getPlatformSubscriptionAdministrationState,
  previewPlatformPlanChange
} from "./platform-admin-client";
